import { memo, useRef, useState } from "react";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  MapPin, 
  Camera,
  Save,
  CreditCard,
  Trash2
} from "lucide-react";
import { Button } from "@/app/components/button";
import { BottomNav } from "./bottom-nav";
import { formatCPF, formatPhoneNumber, formatCEP, getCpfMaskLength, getCpfPlaceholder, validateCPF, validateEmail, validatePhone, validateCEP } from "@/config/locale";
import * as authClient from "@/app/api/auth-client";
import type { Profile } from "@/app/api/auth-client";

interface ProfileData {
  fullName: string;
  preferredName: string;
  cpf: string;
  email: string;
  phone: string;
  birthDate: string;
  gender: "male" | "female" | "other" | "prefer-not-to-say";
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    cep: string;
  };
  profileImage?: string;
}

interface EditProfilePageProps {
  onBack: () => void;
  onSave: (data: ProfileData) => void | Promise<void>;
  /** Called after profile photo upload so the app can refresh header avatar. */
  onProfileUpdated?: (profile: Profile) => void;
  initialData?: ProfileData;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

interface FormErrors {
  fullName?: string;
  cpf?: string;
  email?: string;
  phone?: string;
  birthDate?: string;
  street?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  cep?: string;
}

/** Empty form when parent does not pass `initialData` (e.g. edit profile without loaded row). */
const EMPTY_PROFILE: ProfileData = {
  fullName: "",
  preferredName: "",
  cpf: "",
  email: "",
  phone: "",
  birthDate: "",
  gender: "prefer-not-to-say",
  address: {
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "SP",
    cep: "",
  },
};

const BRAZILIAN_STATES = [
  { code: "AC", name: "Acre" },
  { code: "AL", name: "Alagoas" },
  { code: "AP", name: "Amapá" },
  { code: "AM", name: "Amazonas" },
  { code: "BA", name: "Bahia" },
  { code: "CE", name: "Ceará" },
  { code: "DF", name: "Distrito Federal" },
  { code: "ES", name: "Espírito Santo" },
  { code: "GO", name: "Goiás" },
  { code: "MA", name: "Maranhão" },
  { code: "MT", name: "Mato Grosso" },
  { code: "MS", name: "Mato Grosso do Sul" },
  { code: "MG", name: "Minas Gerais" },
  { code: "PA", name: "Pará" },
  { code: "PB", name: "Paraíba" },
  { code: "PR", name: "Paraná" },
  { code: "PE", name: "Pernambuco" },
  { code: "PI", name: "Piauí" },
  { code: "RJ", name: "Rio de Janeiro" },
  { code: "RN", name: "Rio Grande do Norte" },
  { code: "RS", name: "Rio Grande do Sul" },
  { code: "RO", name: "Rondônia" },
  { code: "RR", name: "Roraima" },
  { code: "SC", name: "Santa Catarina" },
  { code: "SP", name: "São Paulo" },
  { code: "SE", name: "Sergipe" },
  { code: "TO", name: "Tocantins" },
];

export const EditProfilePage = memo(function EditProfilePage({
  onBack,
  onSave,
  onProfileUpdated,
  initialData = EMPTY_PROFILE,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: EditProfilePageProps) {
  const [formData, setFormData] = useState<ProfileData>(initialData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const initials = (() => {
    const n = formData.fullName.trim();
    if (n) {
      return n
        .split(/\s+/)
        .filter(Boolean)
        .map((name) => name[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    const em = formData.email.trim();
    if (em.length >= 2) return em.slice(0, 2).toUpperCase();
    return "?";
  })();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Full name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Nome completo é obrigatório";
    } else if (formData.fullName.trim().split(" ").length < 2) {
      newErrors.fullName = "Digite nome e sobrenome";
    }

    // CPF validation
    if (!formData.cpf) {
      newErrors.cpf = "CPF é obrigatório";
    } else if (!validateCPF(formData.cpf)) {
      newErrors.cpf = "CPF inválido";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = "Telefone é obrigatório";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Telefone inválido";
    }

    // Birth date validation
    if (!formData.birthDate) {
      newErrors.birthDate = "Data de nascimento é obrigatória";
    } else {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        newErrors.birthDate = "Você deve ter pelo menos 18 anos";
      } else if (age > 120) {
        newErrors.birthDate = "Data de nascimento inválida";
      }
    }

    // Address validations
    if (!formData.address.street.trim()) {
      newErrors.street = "Rua é obrigatória";
    }

    if (!formData.address.number.trim()) {
      newErrors.number = "Número é obrigatório";
    }

    if (!formData.address.neighborhood.trim()) {
      newErrors.neighborhood = "Bairro é obrigatório";
    }

    if (!formData.address.city.trim()) {
      newErrors.city = "Cidade é obrigatória";
    }

    if (!formData.address.state) {
      newErrors.state = "Estado é obrigatório";
    }

    if (!formData.address.cep) {
      newErrors.cep = "CEP é obrigatório";
    } else if (!validateCEP(formData.address.cep)) {
      newErrors.cep = "CEP inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const result = onSave(formData);
      if (result && typeof (result as Promise<unknown>).then === "function") {
        await result;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoUploadError(null);
    setPhotoUploadLoading(true);
    try {
      const profile = await authClient.uploadProfilePhoto(file);
      setFormData((prev) => ({ ...prev, profileImage: profile.profile_image_url ?? "" }));
      onProfileUpdated?.(profile);
    } catch (err) {
      setPhotoUploadError(err instanceof Error ? err.message : "Não foi possível enviar a foto.");
    } finally {
      setPhotoUploadLoading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoUploadError(null);
    setPhotoUploadLoading(true);
    try {
      const profile = await authClient.saveProfile({ profile_image_url: "" });
      setFormData((prev) => ({ ...prev, profileImage: "" }));
      onProfileUpdated?.(profile);
    } catch (err) {
      setPhotoUploadError(err instanceof Error ? err.message : "Não foi possível remover a foto.");
    } finally {
      setPhotoUploadLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");
    setFormData({ ...formData, phone: digits });
  };

  const handleCPFChange = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");
    setFormData({ ...formData, cpf: digits });
  };

  const handleCEPChange = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");
    setFormData({ 
      ...formData, 
      address: { ...formData.address, cep: digits }
    });
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Voltar"
              >
                <ArrowLeft className="w-6 h-6 text-white" />
              </button>
              <div>
                <h1 className="text-xl text-white">Editar Perfil</h1>
                <p className="text-sm text-white/80">Atualize suas informações</p>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-4 py-2 bg-white text-primary rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>Salvando...</>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Salvar
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Photo Section */}
        <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            Foto de Perfil
          </h3>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/svg+xml"
            className="hidden"
            onChange={handlePhotoUpload}
          />
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-3xl font-medium overflow-hidden">
                {formData.profileImage ? (
                  <img
                    src={formData.profileImage}
                    alt={formData.fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <button
                type="button"
                disabled={photoUploadLoading}
                onClick={() => photoInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center shadow-lg transition-colors disabled:opacity-50"
                aria-label="Alterar foto"
              >
                <Camera className="w-4 h-4 text-primary-foreground" />
              </button>
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-2">
                Escolha uma foto para seu perfil. JPG, PNG, WebP, HEIC ou SVG (convertido para JPEG), máx. 5MB.
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={photoUploadLoading}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {photoUploadLoading ? "Enviando…" : "Escolher Foto"}
                </Button>
                {formData.profileImage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={photoUploadLoading}
                    onClick={handleRemovePhoto}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remover foto
                  </Button>
                )}
                {photoUploadError && (
                  <span className="text-sm text-destructive">{photoUploadError}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Informações Pessoais
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="text-sm font-medium mb-2 block">
                Nome Completo *
              </label>
              <input
                type="text"
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.fullName ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="Digite seu nome completo"
              />
              {errors.fullName && (
                <p className="text-sm text-red-500 mt-1">{errors.fullName}</p>
              )}
            </div>

            {/* Preferred Name */}
            <div>
              <label htmlFor="preferredName" className="text-sm font-medium mb-2 block">
                Nome de Preferência
              </label>
              <input
                type="text"
                id="preferredName"
                value={formData.preferredName}
                onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Como prefere ser chamado(a). Opcional."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nome social ou como prefere ser chamado(a)
              </p>
            </div>

            {/* CPF */}
            <div>
              <label htmlFor="cpf" className="text-sm font-medium mb-2 block">
                CPF *
              </label>
              <input
                type="text"
                id="cpf"
                value={formatCPF(formData.cpf)}
                onChange={(e) => handleCPFChange(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.cpf ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder={getCpfPlaceholder()}
                maxLength={getCpfMaskLength()}
              />
              {errors.cpf && (
                <p className="text-sm text-red-500 mt-1">{errors.cpf}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                <CreditCard className="w-3 h-3 inline mr-1" />
                O CPF não pode ser alterado após a verificação
              </p>
            </div>

            {/* Birth date (gênero oculto por enquanto; enviamos prefer-not-to-say no save) */}
            <div>
              <label htmlFor="birthDate" className="text-sm font-medium mb-2 block">
                Data de Nascimento *
              </label>
              <input
                type="date"
                id="birthDate"
                value={formData.birthDate}
                onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.birthDate ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
              />
              {errors.birthDate && (
                <p className="text-sm text-red-500 mt-1">{errors.birthDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Informações de Contato
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="text-sm font-medium mb-2 block">
                E-mail *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.email ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="seu.email@exemplo.com"
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="text-sm font-medium mb-2 block">
                Telefone/Celular *
              </label>
              <input
                type="tel"
                id="phone"
                value={formatPhoneNumber(formData.phone)}
                onChange={(e) => handlePhoneChange(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.phone ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="(11) 98765-4321"
                maxLength={15}
              />
              {errors.phone && (
                <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Endereço Principal
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* CEP */}
            <div>
              <label htmlFor="cep" className="text-sm font-medium mb-2 block">
                CEP *
              </label>
              <input
                type="text"
                id="cep"
                value={formatCEP(formData.address.cep)}
                onChange={(e) => handleCEPChange(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.cep ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="00000-000"
                maxLength={9}
              />
              {errors.cep && (
                <p className="text-sm text-red-500 mt-1">{errors.cep}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Digite o CEP para preencher automaticamente
              </p>
            </div>

            {/* Street and Number */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="street" className="text-sm font-medium mb-2 block">
                  Rua *
                </label>
                <input
                  type="text"
                  id="street"
                  value={formData.address.street}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, street: e.target.value }
                  })}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.street ? "border-red-500" : "border-border"
                  } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                  placeholder="Nome da rua"
                />
                {errors.street && (
                  <p className="text-sm text-red-500 mt-1">{errors.street}</p>
                )}
              </div>

              <div>
                <label htmlFor="number" className="text-sm font-medium mb-2 block">
                  Número *
                </label>
                <input
                  type="text"
                  id="number"
                  value={formData.address.number}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, number: e.target.value }
                  })}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.number ? "border-red-500" : "border-border"
                  } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                  placeholder="123"
                />
                {errors.number && (
                  <p className="text-sm text-red-500 mt-1">{errors.number}</p>
                )}
              </div>
            </div>

            {/* Complement */}
            <div>
              <label htmlFor="complement" className="text-sm font-medium mb-2 block">
                Complemento
              </label>
              <input
                type="text"
                id="complement"
                value={formData.address.complement || ""}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { ...formData.address, complement: e.target.value }
                })}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Apto, bloco, etc. (opcional)"
              />
            </div>

            {/* Neighborhood */}
            <div>
              <label htmlFor="neighborhood" className="text-sm font-medium mb-2 block">
                Bairro *
              </label>
              <input
                type="text"
                id="neighborhood"
                value={formData.address.neighborhood}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { ...formData.address, neighborhood: e.target.value }
                })}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.neighborhood ? "border-red-500" : "border-border"
                } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                placeholder="Nome do bairro"
              />
              {errors.neighborhood && (
                <p className="text-sm text-red-500 mt-1">{errors.neighborhood}</p>
              )}
            </div>

            {/* City and State */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="city" className="text-sm font-medium mb-2 block">
                  Cidade *
                </label>
                <input
                  type="text"
                  id="city"
                  value={formData.address.city}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, city: e.target.value }
                  })}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.city ? "border-red-500" : "border-border"
                  } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                  placeholder="Nome da cidade"
                />
                {errors.city && (
                  <p className="text-sm text-red-500 mt-1">{errors.city}</p>
                )}
              </div>

              <div>
                <label htmlFor="state" className="text-sm font-medium mb-2 block">
                  Estado *
                </label>
                <select
                  id="state"
                  value={formData.address.state}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, state: e.target.value }
                  })}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.state ? "border-red-500" : "border-border"
                  } bg-background focus:outline-none focus:ring-2 focus:ring-primary/50`}
                >
                  <option value="">Selecione</option>
                  {BRAZILIAN_STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.code}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-sm text-red-500 mt-1">{errors.state}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSaving}
            className="flex-1"
          >
            {isSaving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      </form>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (tab === "home" || tab === "favorites") onTabChange(tab);
        }}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
});
